import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Bell, ChefHat, Clock, Home, LogOut, Plus, RefreshCw, ShieldCheck, Table2, UserRound } from "lucide-react";
import { api, API_BASE, formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

function badgeClass(status) {
  const key = String(status || "NEW").toUpperCase();
  return {
    NEW: "bg-amber-100 text-amber-800 border-amber-200",
    ACCEPTED: "bg-sky-100 text-sky-800 border-sky-200",
    PREPARING: "bg-violet-100 text-violet-800 border-violet-200",
    READY: "bg-emerald-100 text-emerald-800 border-emerald-200",
    SERVED: "bg-slate-100 text-slate-700 border-slate-200",
    DONE: "bg-slate-100 text-slate-700 border-slate-200",
    CANCELLED: "bg-red-100 text-red-700 border-red-200",
    BILLING: "bg-orange-100 text-orange-800 border-orange-200",
  }[key] || "bg-slate-100 text-slate-700 border-slate-200";
}

function statusLabel(status) {
  const key = normalizeStatus(status);
  return {
    NEW: "NEW",
    ACCEPTED: "CONFIRMED",
    PREPARING: "PREPARING",
    READY: "FURNISHED",
    SERVED: "SERVED",
    DONE: "DONE",
    BILLING: "BILLING",
    CANCELLED: "CANCELLED",
  }[key] || key;
}

function normalizeStatus(status) {
  const key = String(status || "NEW").toUpperCase();
  return {
    CONFIRMED: "ACCEPTED",
    ACCEPT: "ACCEPTED",
    FURNISHED: "READY",
    READY: "READY",
    DONE: "DONE",
    DELIVERED: "SERVED",
  }[key] || key;
}

function staffRole(user) {
  return String(user?.role || user?.hotel_role || user?.staff_role || "").toUpperCase();
}

function routeAllowsRole(routeMode, role) {
  const normalized = String(role || "").toUpperCase();
  if (routeMode === "kitchen") return normalized === "KITCHEN";
  return normalized === "WAITER" || normalized === "HOTEL_ADMIN";
}

function itemStatus(item) {
  return normalizeStatus(item?.hotel_status || item?.item_status || item?.status || "NEW");
}

function elapsed(mins) {
  const n = Number(mins || 0);
  if (n < 1) return "Just now";
  if (n < 60) return `${n} min ago`;
  return `${Math.floor(n / 60)}h ${n % 60}m ago`;
}

function orderTotal(order) {
  return Number(order?.total || (order?.items || []).reduce((sum, item) => sum + Number(item.line_total || 0), 0));
}

function orderItemCount(order) {
  return Number(order?.item_count ?? (order?.items || []).reduce((sum, item) => sum + Number(item.qty || 0), 0));
}

function orderCustomerLabel(order) {
  return order?.customer_label || order?.shopper_name || order?.shipping_address?.name || order?.shopper_phone || "Guest";
}

function orderCustomerPhone(order) {
  return order?.customer_phone || order?.shopper_phone || order?.shipping_address?.phone || "";
}

function orderTableNumber(order) {
  return order?.table_number || order?.table?.tableNumber || "";
}

function orderAnchorId(order) {
  return `order-${order?.id || orderTableNumber(order) || "row"}`;
}

function isActiveHotelOrder(order) {
  return !["SERVED", "DONE", "CANCELLED", "COMPLETED"].includes(normalizeStatus(order?.hotelStatus || order?.status));
}

function isTodayOrder(order) {
  const raw = order?.created_at || order?.createdAt || order?.created;
  if (!raw) return false;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate();
}

function isTodayOpenOrder(order) {
  return isTodayOrder(order) && isActiveHotelOrder(order);
}

function nextOrderAction(status, isKitchen) {
  const current = normalizeStatus(status);
  if (current === "NEW") return { status: "ACCEPTED", label: "Confirm", tone: "dark" };
  if (current === "ACCEPTED") return { status: "PREPARING", label: "Preparing", tone: "purple" };
  if (current === "PREPARING") return { status: "READY", label: "Furnished", tone: "green" };
  if (current === "READY") return { status: isKitchen ? "DONE" : "SERVED", label: isKitchen ? "Done" : "Served", tone: isKitchen ? "dark" : "dark" };
  if (!isKitchen && current === "SERVED") return { status: "BILLING", label: "Bill", tone: "outline" };
  return null;
}

function nextItemAction(status, isKitchen) {
  const current = normalizeStatus(status);
  if (current === "NEW") return { status: "ACCEPTED", label: "Confirm", tone: "dark" };
  if (current === "ACCEPTED") return { status: "PREPARING", label: "Preparing", tone: "purple" };
  if (current === "PREPARING") return { status: "READY", label: "Furnished", tone: "green" };
  if (!isKitchen && current === "READY") return { status: "SERVED", label: "Served", tone: "dark" };
  if (isKitchen && current === "READY") return { status: "DONE", label: "Done", tone: "dark" };
  return null;
}

function actionClass(tone) {
  if (tone === "green") return "rounded-sm bg-emerald-600 hover:bg-emerald-700";
  if (tone === "purple") return "rounded-sm bg-violet-600 hover:bg-violet-700";
  if (tone === "outline") return "rounded-sm";
  return "rounded-sm bg-slate-950 hover:bg-slate-800";
}

export default function HotelStaffApp({ forcedClientRef = "", forcedMode = "" }) {
  const { clientRef = "" } = useParams();
  const location = useLocation();
  const routeClientRef = forcedClientRef || clientRef;
  const routeMode = forcedMode || (location.pathname.startsWith("/kitchen") ? "kitchen" : "staff");
  const hotelTokenKey = routeMode === "kitchen" ? "hotel_kitchen_access_token" : "hotel_staff_access_token";
  const hotelRefreshTokenKey = routeMode === "kitchen" ? "hotel_kitchen_refresh_token" : "hotel_staff_refresh_token";
  const [token, setToken] = useState(() => localStorage.getItem(hotelTokenKey) || "");
  const [user, setUser] = useState(null);
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [login, setLogin] = useState({ client_ref: routeClientRef, username: "", password: "", pin_code: "" });
  const [manualForm, setManualForm] = useState({ table_number: "", phone: "", name: "", product_id: "", qty: 1, notes: "" });
  const [manualBusy, setManualBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("todayOpen");
  const seenNewOrdersRef = useRef(new Set());
  const alertsReadyRef = useRef(false);
  const roleRef = useRef("");
  const routeModeRef = useRef(routeMode);

  const playAlert = useCallback(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.value = 0.08;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      window.setTimeout(() => {
        osc.stop();
        ctx.close();
      }, 220);
    } catch {
      // Browser/device may block sound until the first user interaction.
    }
  }, []);

  const hotelAuth = useCallback((accessToken = token) => ({
    headers: { Authorization: `Bearer ${accessToken}` },
  }), [token]);

  useEffect(() => {
    roleRef.current = staffRole(user);
  }, [user]);

  useEffect(() => {
    routeModeRef.current = routeMode;
  }, [routeMode]);

  useEffect(() => {
    setToken(localStorage.getItem(hotelTokenKey) || "");
  }, [hotelTokenKey]);

  useEffect(() => {
    if (routeClientRef) {
      setLogin((current) => ({ ...current, client_ref: routeClientRef }));
    }
  }, [routeClientRef]);

  const clearSession = useCallback(() => {
    const savedStaffToken = localStorage.getItem(hotelTokenKey);
    if (savedStaffToken && localStorage.getItem("access_token") === savedStaffToken) {
      localStorage.removeItem("access_token");
    }
    localStorage.removeItem(hotelTokenKey);
    localStorage.removeItem(hotelRefreshTokenKey);
    localStorage.removeItem("hotel_access_token");
    setToken("");
    setUser(null);
    setClient(null);
    setOrders([]);
    setTables([]);
    setMenuItems([]);
  }, [hotelTokenKey, hotelRefreshTokenKey]);

  // Waiters/kitchen staff working a full shift used to hit a hard, silent
  // 12h logout mid-service with no recovery - /hotel/auth/login now issues
  // a refresh token too, and the generic /auth/refresh endpoint knows how
  // to reissue a hotel-scoped access token for it. Called directly with
  // plain axios (not the shared `api` client) to avoid recursing into that
  // client's own unrelated refresh interceptor.
  const refreshHotelToken = useCallback(async () => {
    const rt = localStorage.getItem(hotelRefreshTokenKey);
    if (!rt) return "";
    try {
      const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: rt }, { withCredentials: true });
      if (!data?.access_token) return "";
      localStorage.setItem(hotelTokenKey, data.access_token);
      if (data.refresh_token) localStorage.setItem(hotelRefreshTokenKey, data.refresh_token);
      setToken(data.access_token);
      return data.access_token;
    } catch {
      return "";
    }
  }, [hotelRefreshTokenKey, hotelTokenKey]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [meRes, orderRes, tableRes] = await Promise.all([
        api.get("/hotel/auth/me", hotelAuth(token)),
        api.get("/hotel/orders", hotelAuth(token)),
        api.get("/hotel/tables", hotelAuth(token)).catch(() => ({ data: [] })),
      ]);
      const actor = meRes.data?.actor?.staff || meRes.data?.actor || null;
      const actorRole = staffRole(actor);
      if (!routeAllowsRole(routeModeRef.current, actorRole)) {
        clearSession();
        toast.error(routeModeRef.current === "kitchen" ? "Please login with a kitchen user." : "Please login with a waiter user.");
        return;
      }
      roleRef.current = actorRole;
      setUser(actor);
      setClient(meRes.data?.client || null);
      const nextOrders = orderRes.data || [];
      const alertAsKitchen = routeModeRef.current === "kitchen" || roleRef.current === "KITCHEN";
      const activeNewIds = nextOrders
        .filter((o) => String(o.hotelStatus || "").toUpperCase() === "NEW")
        .filter((o) => alertAsKitchen ? !o.kitchenAcknowledged : !o.waiterAcknowledged)
        .map((o) => o.id);
      const seen = seenNewOrdersRef.current;
      const hasFresh = activeNewIds.some((id) => id && !seen.has(id));
      activeNewIds.forEach((id) => id && seen.add(id));
      if (hasFresh && alertsReadyRef.current) {
        playAlert();
        toast.info("New hotel order received");
      }
      alertsReadyRef.current = true;
      setOrders(nextOrders);
      setTables(tableRes.data || []);
      if (routeModeRef.current !== "kitchen" && roleRef.current !== "KITCHEN") {
        const menuRes = await api.get("/hotel/menu-items", hotelAuth(token)).catch(() => ({ data: [] }));
        setMenuItems(menuRes.data || []);
      } else {
        setMenuItems([]);
      }
    } catch (e) {
      if (e.response?.status === 401) {
        const newToken = await refreshHotelToken();
        if (newToken) return; // token state updated - the [token] dependency re-triggers load()
      }
      toast.error(formatErr(e.response?.data?.detail));
      clearSession();
    }
  }, [clearSession, hotelAuth, playAlert, refreshHotelToken, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = setInterval(load, 7000);
    return () => clearInterval(timer);
  }, [load]);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/hotel/auth/login", login);
      const loggedRole = staffRole(data.user);
      if (!routeAllowsRole(routeMode, loggedRole)) {
        toast.error(routeMode === "kitchen" ? "This is not a kitchen user. Login with kitchen account." : "This is not a waiter account.");
        return;
      }
      localStorage.setItem(hotelTokenKey, data.access_token);
      if (data.refresh_token) localStorage.setItem(hotelRefreshTokenKey, data.refresh_token);
      setToken(data.access_token);
      setUser(data.user);
      setClient(data.client);
      toast.success("Hotel login successful");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    clearSession();
  };

  const setStatus = async (order, status) => {
    try {
      await api.put(`/hotel/orders/${order.id}/status`, { status }, hotelAuth());
      toast.success(`Order marked ${statusLabel(status).toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const setLineStatus = async (order, item, status) => {
    const itemRef = item.line_id || item.id || item.product_id || item.menuItemId || item.name;
    if (!itemRef) {
      toast.error("Item reference missing");
      return;
    }
    try {
      await api.put(`/hotel/orders/${order.id}/items/${encodeURIComponent(itemRef)}/status`, { status }, hotelAuth());
      toast.success(`Item marked ${statusLabel(status).toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const createManualOrder = async (e) => {
    e.preventDefault();
    if (!manualForm.table_number || !manualForm.product_id) {
      toast.error("Select table and dish");
      return;
    }
    if (!manualForm.phone) {
      toast.error("Enter customer mobile so separate bills do not get mixed");
      return;
    }
    setManualBusy(true);
    try {
      await api.post("/hotel/orders", {
        table_number: manualForm.table_number,
        phone: manualForm.phone,
        name: manualForm.name,
        notes: manualForm.notes,
        items: [{ product_id: manualForm.product_id, qty: Number(manualForm.qty || 1) }],
      }, hotelAuth());
      toast.success("Dish added to table bill");
      setManualForm((prev) => ({ ...prev, product_id: "", qty: 1, notes: "" }));
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setManualBusy(false);
    }
  };

  const ack = async (order) => {
    try {
      await api.put(`/hotel/orders/${order.id}/acknowledge`, {}, hotelAuth());
      toast.success("Order acknowledged");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const visibleOrders = useMemo(() => {
    const rows = [...orders].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    if (filter === "all") return rows;
    if (filter === "ready") return rows.filter((o) => isTodayOrder(o) && normalizeStatus(o.hotelStatus || o.status) === "READY");
    if (filter === "done") return rows.filter((o) => isTodayOrder(o) && ["SERVED", "DONE", "CANCELLED", "COMPLETED"].includes(normalizeStatus(o.hotelStatus || o.status)));
    return rows.filter(isTodayOpenOrder);
  }, [filter, orders]);
  const role = staffRole(user);
  const isKitchenRole = role === "KITCHEN";
  const isKitchen = routeMode === "kitchen" || isKitchenRole;
  const screenLabel = isKitchen ? "Kitchen display" : role === "HOTEL_ADMIN" ? "Hotel staff dashboard" : "Waiter dashboard";
  const profileName = user?.name || user?.username || "Staff user";
  const profileDetail = [
    user?.username ? `@${user.username}` : "",
    role || "STAFF",
    user?.mobile || user?.phone,
  ].filter(Boolean).join(" - ");
  const todayOpenOrders = useMemo(
    () => orders.filter(isTodayOpenOrder).sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))),
    [orders]
  );
  const todayReadyOrders = useMemo(
    () => orders.filter((order) => isTodayOrder(order) && normalizeStatus(order.hotelStatus || order.status) === "READY"),
    [orders]
  );
  const activeOrders = todayOpenOrders;

  if (!token) {
    return (
      <main className="min-h-screen bg-slate-950 px-4 py-8 text-white">
        <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-5xl items-center justify-center">
          <div className="grid w-full overflow-hidden rounded-sm border border-white/10 bg-white shadow-2xl md:grid-cols-[1fr_0.9fr]">
            <section className="bg-slate-950 p-8 md:p-12">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-blue-500"><Table2 className="h-6 w-6" /></div>
              <h1 className="mt-8 font-display text-4xl font-extrabold tracking-tight">{routeMode === "kitchen" ? "Kitchen Display Login" : "Hotel / Restaurant Staff"}</h1>
              <p className="mt-4 text-slate-300">{routeMode === "kitchen" ? "Kitchen sees every order, newest first, without manual order entry." : "Waiters see only assigned tables. Kitchen sees every order, newest first."}</p>
              <div className="mt-8 grid gap-3 text-sm text-slate-300">
                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-300" /> Role-based access</div>
                <div className="flex items-center gap-2"><Bell className="h-4 w-4 text-amber-300" /> New order highlighting</div>
                <div className="flex items-center gap-2"><ChefHat className="h-4 w-4 text-sky-300" /> Kitchen-ready workflow</div>
              </div>
            </section>
            <section className="p-8 text-slate-950 md:p-12">
              <h2 className="font-display text-2xl font-extrabold tracking-tight">Staff login</h2>
              <form onSubmit={doLogin} className="mt-6 space-y-4">
                <div>
                  <Label>Hotel reference</Label>
                  <Input
                    placeholder="Client name, slug or id"
                    value={login.client_ref}
                    onChange={(e) => setLogin({ ...login, client_ref: e.target.value })}
                    required
                    readOnly={!!routeClientRef}
                    className={routeClientRef ? "bg-slate-100 text-slate-500" : ""}
                  />
                  {routeClientRef ? <p className="mt-1 text-xs text-slate-500">Detected from staff link.</p> : null}
                </div>
                <div><Label>Username / mobile</Label><Input value={login.username} onChange={(e) => setLogin({ ...login, username: e.target.value })} required /></div>
                <div><Label>Password</Label><Input type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} /></div>
                <div><Label>Fast PIN</Label><Input value={login.pin_code} onChange={(e) => setLogin({ ...login, pin_code: e.target.value })} /></div>
                <Button disabled={busy} className="w-full rounded-sm bg-slate-950">Sign in</Button>
              </form>
            </section>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main id="top" className="min-h-screen bg-slate-100 pb-20 sm:pb-0">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{screenLabel}</div>
            <h1 className="font-display text-xl font-extrabold tracking-tight text-slate-950">{client?.name || "Hotel / Restaurant"}</h1>
            <p className="text-xs font-semibold text-slate-500">Today open orders only by default</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[210px] rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-left">
              <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Logged in user</div>
              <div className="truncate text-sm font-black text-slate-950">{profileName}</div>
              <div className="truncate text-[11px] font-semibold text-slate-500">{profileDetail || "STAFF"}</div>
            </div>
            <Button variant="outline" className="rounded-sm" onClick={load}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
            <Button variant="outline" className="rounded-sm" onClick={logout}><LogOut className="mr-2 h-4 w-4" />Logout</Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-5 grid gap-3 sm:grid-cols-4">
          <div className="border border-slate-200 bg-white p-4">
            <UserRound className="mb-2 h-4 w-4 text-slate-500" />
            <div className="font-bold">{profileName}</div>
            <div className="text-xs font-semibold text-slate-500">{role || "STAFF"}</div>
            {user?.username ? <div className="mt-1 text-xs text-slate-500">@{user.username}</div> : null}
          </div>
          <div className="border border-slate-200 bg-white p-4"><Table2 className="mb-2 h-4 w-4 text-slate-500" /><div className="text-2xl font-black">{activeOrders.length}</div><div className="text-xs text-slate-500">Today open bills</div></div>
          <div className="border border-slate-200 bg-white p-4"><ClipboardCount value={todayOpenOrders.filter((o) => normalizeStatus(o.hotelStatus || o.status) === "NEW").length} label="Today new orders" /></div>
          <div className="border border-slate-200 bg-white p-4"><ClipboardCount value={todayReadyOrders.length} label="Today furnished" /></div>
        </div>

        {activeOrders.length ? (
          <section className="mb-5">
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Today's open orders</div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {activeOrders.map((order) => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => document.getElementById(orderAnchorId(order))?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="border border-amber-300 bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-black text-slate-950">Table {orderTableNumber(order) || "Online"}</span>
                    <span className={`border px-2 py-1 text-[10px] font-black uppercase ${badgeClass(order.hotelStatus)}`}>{statusLabel(order.hotelStatus)}</span>
                  </div>
                  <div className="mt-1 truncate text-sm font-semibold text-slate-700">{orderCustomerLabel(order)}</div>
                  {orderCustomerPhone(order) ? <div className="truncate text-xs text-slate-500">{orderCustomerPhone(order)}</div> : null}
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span>{orderItemCount(order)} items</span>
                    <span>{elapsed(order.elapsedMinutes)}</span>
                  </div>
                  <div className="mt-2 font-black text-slate-950">₹ {orderTotal(order).toLocaleString()}</div>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {!isKitchen && (
          <section id="manual-order" className="mb-5 scroll-mt-28 border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <Plus className="h-4 w-4 text-slate-700" />
              <div>
                <h2 className="font-display text-lg font-extrabold tracking-tight text-slate-950">Add table order</h2>
                <p className="text-xs text-slate-500">Use this when the customer gives order verbally. Mobile number keeps Ravi and Lakshmi as separate bills on the same table.</p>
              </div>
            </div>
            <form onSubmit={createManualOrder} className="grid gap-3 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1.6fr_0.7fr_auto]">
              <select
                className="h-10 border border-slate-300 bg-white px-3 text-sm lg:col-span-2"
                value=""
                onChange={(e) => {
                  const order = activeOrders.find((row) => row.id === e.target.value);
                  if (!order) return;
                  setManualForm({
                    ...manualForm,
                    table_number: orderTableNumber(order),
                    phone: orderCustomerPhone(order),
                    name: orderCustomerLabel(order) === "Guest" ? "" : orderCustomerLabel(order),
                  });
                }}
              >
                <option value="">New customer / choose running bill</option>
                {activeOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    Table {orderTableNumber(order) || "-"} - {orderCustomerLabel(order)} {orderCustomerPhone(order) ? `- ${orderCustomerPhone(order)}` : ""} - ₹ {orderTotal(order).toLocaleString()}
                  </option>
                ))}
              </select>
              <select
                className="h-10 border border-slate-300 bg-white px-3 text-sm"
                value={manualForm.table_number}
                onChange={(e) => setManualForm({ ...manualForm, table_number: e.target.value })}
                required
              >
                <option value="">Select table</option>
                {tables.map((table) => (
                  <option key={table.id || table.tableNumber} value={table.tableNumber}>{table.tableNumber} {table.section ? `- ${table.section}` : ""}</option>
                ))}
              </select>
              <Input placeholder="Mobile number" value={manualForm.phone} onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })} />
              <Input placeholder="Customer name" value={manualForm.name} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })} />
              <select
                className="h-10 border border-slate-300 bg-white px-3 text-sm"
                value={manualForm.product_id}
                onChange={(e) => setManualForm({ ...manualForm, product_id: e.target.value })}
                required
              >
                <option value="">Select dish</option>
                {menuItems.map((item) => (
                  <option key={item.id} value={item.id}>{item.name} - ₹ {Number(item.price || 0).toLocaleString()}</option>
                ))}
              </select>
              <Input type="number" min="1" max="99" value={manualForm.qty} onChange={(e) => setManualForm({ ...manualForm, qty: e.target.value })} />
              <Button disabled={manualBusy} className="rounded-sm bg-slate-950">Add</Button>
              <Input className="lg:col-span-7" placeholder="Special cooking note, optional" value={manualForm.notes} onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} />
            </form>
          </section>
        )}

        <div className="mb-4 flex flex-wrap gap-2">
          {[
            { key: "todayOpen", label: "Today open" },
            { key: "ready", label: "Ready today" },
            { key: "done", label: "Done today" },
            { key: "all", label: "All history" },
          ].map((item) => (
            <button key={item.key} onClick={() => setFilter(item.key)} className={`border px-4 py-2 text-sm font-bold ${filter === item.key ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-700"}`}>{item.label}</button>
          ))}
        </div>

        {activeOrders.length ? (
          <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
            {activeOrders.map((order) => (
              <a key={order.id} href={`#${orderAnchorId(order)}`} className="shrink-0 border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-black text-slate-950">
                Table {orderTableNumber(order) || "-"} - {orderCustomerLabel(order)}
              </a>
            ))}
          </div>
        ) : null}

        <div className="grid gap-4">
          {visibleOrders.map((order) => {
            const acknowledged = isKitchen ? order.kitchenAcknowledged : order.waiterAcknowledged;
            const late = Number(order.elapsedMinutes || 0) >= 12 && !["SERVED", "DONE", "CANCELLED"].includes(String(order.hotelStatus || "").toUpperCase());
            const orderAction = nextOrderAction(order.hotelStatus, isKitchen);
            return (
              <article id={orderAnchorId(order)} key={order.id} className={`scroll-mt-28 border bg-white p-4 shadow-sm sm:p-5 ${!acknowledged ? "border-amber-300 shadow-amber-100" : late ? "border-red-300" : "border-slate-200"}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Table {order.table_number || order.table?.tableNumber || "Online"} - {order.order_no}</div>
                    <h2 className="mt-1 font-display text-2xl font-extrabold tracking-tight text-slate-950">{order.shopper_name || order.shopper_phone || "Guest"}</h2>
                    <div className="mt-2 flex items-center gap-2 text-sm text-slate-500"><Clock className="h-4 w-4" /> Ordered {elapsed(order.elapsedMinutes)}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`border px-3 py-1 text-xs font-black uppercase ${badgeClass(order.hotelStatus)}`}>{statusLabel(order.hotelStatus)}</span>
                    {!acknowledged && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => ack(order)}>Seen</Button>}
                    {orderAction && (
                      <Button
                        size="sm"
                        variant={orderAction.tone === "outline" ? "outline" : "default"}
                        className={actionClass(orderAction.tone)}
                        onClick={() => setStatus(order, orderAction.status)}
                      >
                        {orderAction.label}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-4 grid gap-2">
                  {(order.items || []).map((item) => {
                    const lineAction = nextItemAction(itemStatus(item), isKitchen);
                    return (
                    <div key={item.line_id || item.product_id || item.name} className="grid gap-3 border border-slate-100 bg-slate-50 p-3 lg:grid-cols-[1fr_auto]">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-bold text-slate-950">{item.qty} x {item.name}</span>
                          <span className={`border px-2 py-1 text-[11px] font-black uppercase ${badgeClass(itemStatus(item))}`}>{statusLabel(itemStatus(item))}</span>
                          <span className="text-sm font-bold text-slate-950">₹ {Number(item.line_total || 0).toLocaleString()}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          Ordered {item.created_at ? new Date(item.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : elapsed(order.elapsedMinutes)}
                          {item.notes ? ` - ${item.notes}` : ""}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 lg:justify-end">
                        {lineAction && (
                          <Button
                            size="sm"
                            variant={lineAction.tone === "outline" ? "outline" : "default"}
                            className={actionClass(lineAction.tone)}
                            onClick={() => setLineStatus(order, item, lineAction.status)}
                          >
                            {lineAction.label}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => setLineStatus(order, item, "CANCELLED")}>Cancel</Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </article>
            );
          })}
          {!visibleOrders.length && <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">{filter === "todayOpen" ? "No today's open orders." : "No orders in this view."}</div>}
        </div>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-slate-200 bg-white p-2 text-xs font-bold shadow-2xl sm:hidden">
        <a href="#top" className="flex flex-col items-center gap-1 text-slate-700"><Home className="h-4 w-4" />Top</a>
        {!isKitchen && <a href="#manual-order" className="flex flex-col items-center gap-1 text-slate-700"><Plus className="h-4 w-4" />Order</a>}
        <button onClick={load} className="flex flex-col items-center gap-1 text-slate-700"><RefreshCw className="h-4 w-4" />Refresh</button>
        <button onClick={logout} className="flex flex-col items-center gap-1 text-slate-700"><LogOut className="h-4 w-4" />Logout</button>
      </nav>
    </main>
  );
}

function ClipboardCount({ value, label }) {
  return (
    <>
      <Bell className="mb-2 h-4 w-4 text-slate-500" />
      <div className="text-2xl font-black">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </>
  );
}
